import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  IconButton,
} from "@mui/material";
import { Lock, AttachMoney, PeopleAlt } from "@mui/icons-material";
import { PieChart } from "@mui/x-charts";
import React, { useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { WhatsAppNotifyButton } from "../components/WhatsAppNotifyButton";
import { soulManager } from "../data/managers/SoulManager";
import type { CreditApplication } from "../data/models/applications/CreditApplication";
import { calculateMonthlyInstallment } from "../data/models/applications/InstallmentApplication";
import { fmtMoney } from "../utils/NumberTool";

const GREEN = "#00C853";
const DARK = "#0D1420";

const AnalyticsPage: React.FC = () => {
  const [credits, setCredits] = useState<CreditApplication[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [personalLoans, setPersonalLoans] = useState<any[]>([]);
  const [newLoanAmount, setNewLoanAmount] = useState<number>(0);
  const [newLoanDesc, setNewLoanDesc] = useState<string>("");
  const [newLoanDate, setNewLoanDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [savingLoan, setSavingLoan] = useState(false);
  const [leverages, setLeverages] = useState<any[]>([]);
  const [newLevAmount, setNewLevAmount] = useState<number>(0);
  const [newLevRate, setNewLevRate] = useState<number>(2);
  const [newLevDesc, setNewLevDesc] = useState<string>("");
  const [newLevDate, setNewLevDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [newLevType, setNewLevType] = useState<string>("propio");
  const [savingLev, setSavingLev] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [defaultStartDate, setDefaultStartDate] = useState<string | null>(null);
  const [defaultEndDate, setDefaultEndDate] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setTimeout(async () => {
      const rows = await soulManager.getAllApplicationsByAdmin();
      rows?.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setCredits(rows ?? []);
      // Filtro por defecto: último mes
      const today = new Date();
      const lastMonth = new Date();
      lastMonth.setDate(1);
      const todayStr = formatLocalDate(today);
      const lastMonthStr = formatLocalDate(lastMonth);
      setStartDate(lastMonthStr);
      setEndDate(todayStr);
      setDefaultStartDate(lastMonthStr);
      setDefaultEndDate(todayStr);
      // Cargar préstamos propios
      const loans = await soulManager.getPersonalLoans();
      setPersonalLoans(loans ?? []);
      const levs = await soulManager.getLeverages();
      setLeverages(levs ?? []);
      setLoading(false);
    }, 1000);
  }, []);

  function formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: GREEN }} />
      </Box>
    );
  }

  const isInRange = (dateStr: any, start: string | null, end: string | null): boolean => {
    // Manejar Firestore Timestamp o string
    let d: number;
    if (dateStr && typeof dateStr === "object" && dateStr.seconds) {
      d = dateStr.seconds * 1000;
    } else {
      d = new Date(dateStr).getTime();
    }
    if (isNaN(d)) return false;
    if (start) {
      const [sy, sm, sd] = start.split("-").map(Number);
      const from = new Date(sy, sm - 1, sd, 0, 0, 0, 0).getTime();
      if (d < from) return false;
    }
    if (end) {
      const [ey, em, ed] = end.split("-").map(Number);
      const to = new Date(ey, em - 1, ed, 23, 59, 59, 999).getTime();
      if (d > to) return false;
    }
    return true;
  };

  // Capital prestado: solo APPROVED con filtro de fechas
  const approvedInRange = credits.filter((c) =>
    c.status === "APPROVED" && isInRange(c.approvalDate, startDate, endDate)
  );
  const totalLoaned = approvedInRange.reduce((sum, c) => sum + (c.amount ?? 0), 0);
  const totalLoanedWithoutFinished = totalLoaned;

  let totalRecoveredCapitalWithoutFinished = 0;
  let totalRecoveredCapital = 0;
  let totalRecoveredInterest = 0;

  credits.filter((c) => c.status === "APPROVED" || c.status === "FINISHED").forEach((credit) => {
    credit.installments?.filter((i) => i.installmentStatus === "APPROVED" && isInRange(i.createdAt, startDate, endDate))
      .forEach((i) => {
        totalRecoveredCapital += i.capitalAmount ?? 0;
        totalRecoveredInterest += i.interestAmount ?? 0;
      });
    // Saldo activo: todos los pagos del crédito sin filtro de fecha
    if (credit.status === "APPROVED") {
      credit.installments?.filter((i) => i.installmentStatus === "APPROVED")
        .forEach((i) => { totalRecoveredCapitalWithoutFinished += i.capitalAmount ?? 0; });
    }
  });

  // Saldo activo = total prestado activo (todos) - total pagado activo (todos), sin filtro de fechas
  const totalActivoSinFiltro = credits.filter((c) => c.status === "APPROVED").reduce((sum, c) => sum + (c.amount ?? 0), 0);
  const outstandingBalance = totalActivoSinFiltro - totalRecoveredCapitalWithoutFinished;

  // Recaudo total = capital recuperado + intereses (con filtro fechas)
  const totalRecaudo = totalRecoveredCapital + totalRecoveredInterest; // se resta personalLoans abajo

  // Créditos activos
  const approvedActive = credits.filter((c) => c.status === "APPROVED");

  // Promedio de crédito activo
  const promedioCredito = approvedActive.length > 0 ? Math.round(totalActivoSinFiltro / approvedActive.length) : 0;

  // Cliente más rentable (más intereses pagados)
  const clienteRentable = (() => {
    const map: Record<string, { name: string; interest: number }> = {};
    credits.filter((c) => c.status === "APPROVED" || c.status === "FINISHED").forEach((c) => {
      const interest = c.installments?.filter((i) => i.installmentStatus === "APPROVED").reduce((sum, i) => sum + (i.interestAmount ?? 0), 0) ?? 0;
      if (!map[c.applicantName] || map[c.applicantName].interest < interest) {
        map[c.applicantName] = { name: c.applicantName, interest };
      }
    });
    return Object.values(map).sort((a, b) => b.interest - a.interest)[0] ?? null;
  })();
  const recoveryPct = totalLoaned > 0 ? Math.round((totalRecoveredCapital / totalLoaned) * 100) : 0;

  // Préstamos propios del período
  const personalLoansInRange = personalLoans.filter((l) => isInRange(l.date, startDate, endDate));
  const totalPersonalLoans = personalLoansInRange.filter((l) => l.status === "PENDING").reduce((sum, l) => sum + (l.amount ?? 0), 0);
  const totalPersonalLoansPending = personalLoans.filter((l) => l.status === "PENDING").reduce((sum, l) => sum + (l.amount ?? 0), 0);
  const totalRecaudoFinal = totalRecaudo - totalPersonalLoans;
  const totalLeverageActive = leverages.filter((l) => l.status === "ACTIVE").reduce((sum, l) => sum + (l.amount ?? 0), 0);
  const totalLeverageCost = leverages.filter((l) => l.status === "ACTIVE").reduce((sum, l) => sum + (l.amount * (l.interestRate / 100)), 0);

  const today = new Date();

  function getDiffInDays(date1: Date | string, date2: Date | string): number {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
    return (d1.getTime() - d2.getTime()) / (1000 * 3600 * 24);
  }

  const activeOrFinished = credits.filter((c) => c.status === "APPROVED" || c.status === "FINISHED");
  const pending = credits.filter((c) => c.status === "PENDING");
  const finished = credits.filter((c) => c.status === "FINISHED");
  const approved = credits.filter((c) => c.status === "APPROVED");

  const defaulted = activeOrFinished.filter((c) => {
    if (!c.paymentDate) return false;
    const paymentDate = new Date(c.paymentDate);
    const diffDays = getDiffInDays(today, paymentDate);
    return diffDays >= 0 && c.status !== "FINISHED";
  });

  // Iniciales del nombre
  const getInitials = (name: string) => {
    const parts = name?.trim().split(" ") ?? [];
    if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
    return ((parts[0][0] ?? "") + (parts[1][0] ?? "")).toUpperCase();
  };

  const avatarColors = ["#00C853", "#0D1420", "#f59e0b", "#ef4444", "#8B5CF6", "#0EA5E9"];

  return (
    <Box sx={{ pb: 4 }}>

      {/* Filtro de fechas */}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
        <TextField label="Desde" type="date" size="small" InputLabelProps={{ shrink: true }} value={startDate ?? ""} onChange={(e) => setStartDate(e.target.value || null)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
        <TextField label="Hasta" type="date" size="small" InputLabelProps={{ shrink: true }} value={endDate ?? ""} onChange={(e) => setEndDate(e.target.value || null)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
        <Button variant="outlined" size="small" onClick={() => { setStartDate(defaultStartDate); setEndDate(defaultEndDate); }} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: "#e0e0e0", color: "#667788" }}>
          Quitar filtro
        </Button>
      </Stack>

      {/* Capital total prestado - número grande */}
      <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${GREEN}33`, mb: 3, background: `linear-gradient(135deg, ${DARK} 0%, #1a2840 100%)` }}>
        <CardContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#8899AA", letterSpacing: 2, textTransform: "uppercase", mb: 1 }}>
            Capital total prestado
          </Typography>
          <Typography sx={{ fontSize: { xs: 32, md: 44 }, fontWeight: 900, color: "#ffffff", lineHeight: 1, mb: 1 }}>
            {fmtMoney(totalLoaned)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ bgcolor: defaulted.length === 0 ? `${GREEN}33` : "#ef444433", borderRadius: "50px", px: 1.5, py: 0.3 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: defaulted.length === 0 ? GREEN : "#ef4444" }}>
                {defaulted.length === 0 ? "0% mora" : `${defaulted.length} en mora`}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: "#8899AA" }}>
              {defaulted.length === 0 ? "Cartera 100% al día" : "clientes con cuota vencida"}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Cards secundarias */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Saldo activo", value: fmtMoney(outstandingBalance), sublabel: "En calle hoy", color: GREEN, dot: GREEN },
          { label: "Intereses", value: fmtMoney(totalRecoveredInterest), sublabel: "Utilidad recaudada", color: "#f59e0b", dot: "#f59e0b" },
          { label: "Capital recuperado", value: fmtMoney(totalRecoveredCapital), sublabel: `${recoveryPct}% del total prestado devuelto`, color: "#60a5fa", dot: "#60a5fa" },
        ].map((card) => (
          <Grid item xs={12} key={card.label}>
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #f0f0f0", bgcolor: "#fafafa" }}>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#8899AA", letterSpacing: 1, textTransform: "uppercase", mb: 0.5 }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: card.color, lineHeight: 1, mb: 0.3 }}>
                      {card.value}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#8899AA" }}>{card.sublabel}</Typography>
                  </Box>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: card.dot, mt: 0.5 }} />
                </Stack>
                <Box sx={{ mt: 1.5, height: 3, borderRadius: 2, bgcolor: "#e0e0e0" }}>
                  <Box sx={{ height: 3, borderRadius: 2, bgcolor: card.color, width: `${Math.min(100, recoveryPct)}%` }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recaudo total */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #f0f0f0", bgcolor: "#fafafa", mb: 3 }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#8899AA", letterSpacing: 1, textTransform: "uppercase", mb: 0.5 }}>
            Recaudo total del período
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 900, color: "#0D1420", lineHeight: 1 }}>
            {fmtMoney(totalRecaudoFinal)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#8899AA", mt: 0.3 }}>Capital + intereses recaudados</Typography>
        </CardContent>
      </Card>

      {/* Promedio y cliente más rentable */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #f0f0f0", bgcolor: "#fafafa", height: "100%" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#8899AA", letterSpacing: 1, textTransform: "uppercase", mb: 0.5 }}>
                Promedio de crédito
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#00C853", lineHeight: 1 }}>
                {fmtMoney(promedioCredito)}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#8899AA", mt: 0.3 }}>{approvedActive.length} créditos activos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #f0f0f0", bgcolor: "#fafafa", height: "100%" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#8899AA", letterSpacing: 1, textTransform: "uppercase", mb: 0.5 }}>
                Cliente más rentable
              </Typography>
              {clienteRentable ? (
                <>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0D1420", lineHeight: 1.2, mb: 0.3 }}>
                    {clienteRentable.name}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#00C853" }}>
                    {fmtMoney(clienteRentable.interest)} en intereses
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 13, color: "#8899AA" }}>Sin datos</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pendientes de cobro */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #f0f0f0", mb: 3 }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: DARK }}>Pendientes de cobro</Typography>
            <Typography sx={{ fontSize: 12, color: GREEN, fontWeight: 600 }}>{defaulted.length} clientes</Typography>
          </Stack>

          {defaulted.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "#8899AA", textAlign: "center", py: 2 }}>
              🎉 No hay clientes en mora
            </Typography>
          ) : (
            <Stack spacing={1}>
              {defaulted.map((row, idx) => {
                const paymentDate = new Date(row.paymentDate);
                const diffDays = Math.round(getDiffInDays(today, paymentDate));
                const quote = calculateMonthlyInstallment(row.amount, row.interestRate, row.termMonths);
                const avatarColor = avatarColors[idx % avatarColors.length];
                return (
                  <Box key={row.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: "#f7f9f7", border: "1px solid #f0f0f0" }}>
                    {/* Avatar iniciales */}
                    <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: avatarColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "white" }}>{getInitials(row.applicantName)}</Typography>
                    </Box>
                    {/* Info */}
                    <Box flex={1} minWidth={0}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: DARK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row.applicantName}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#8899AA" }}>Cuota {fmtMoney(quote)}</Typography>
                    </Box>
                    {/* Días y WhatsApp */}
                    <Stack alignItems="flex-end" spacing={0.5}>
                      <Box sx={{ bgcolor: diffDays > 5 ? "#ef444418" : "#f59e0b18", border: `1px solid ${diffDays > 5 ? "#ef4444" : "#f59e0b"}44`, borderRadius: "50px", px: 1, py: 0.2 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: diffDays > 5 ? "#ef4444" : "#f59e0b" }}>
                          {diffDays === 0 ? "Hoy" : `${diffDays} día${diffDays > 1 ? "s" : ""}`}
                        </Typography>
                      </Box>
                      <WhatsAppNotifyButton name={row.applicantName} phone={row.phoneOptional!} paymentDate={row.paymentDate} status={row.status} compact />
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Préstamos propios - sección oculta */}
      <Accordion elevation={0} sx={{ borderRadius: 3, border: "1px solid #f0f0f0", "&:before": { display: "none" }, mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Lock sx={{ fontSize: 16, color: DARK }} /><Typography sx={{ fontWeight: 700, fontSize: 15, color: DARK }}>Préstamos propios</Typography></Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, bgcolor: "#f7f9f7", borderRadius: "0 0 12px 12px" }}>
          {/* Stats */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Box sx={{ bgcolor: "white", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase" }}>En el período</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#ef4444" }}>{fmtMoney(totalPersonalLoans)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ bgcolor: "#ef444411", borderRadius: 2, p: 1.5, textAlign: "center", border: "1px solid #ef444433" }}>
                <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase" }}>Pendiente total</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#ef4444" }}>{fmtMoney(totalPersonalLoansPending)}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Agregar nuevo préstamo */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Nuevo préstamo</Typography>
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <TextField fullWidth placeholder="$ Monto" size="small"
              value={newLoanAmount ? newLoanAmount.toLocaleString("es-CO") : ""}
              onChange={(e) => setNewLoanAmount(Number(e.target.value.replace(/\D/g, "")))}
              sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <TextField fullWidth placeholder="Descripción" size="small"
              value={newLoanDesc} onChange={(e) => setNewLoanDesc(e.target.value)}
              sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <TextField fullWidth type="date" size="small"
              value={newLoanDate} onChange={(e) => setNewLoanDate(e.target.value)}
              sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <Button fullWidth variant="contained" disabled={savingLoan || !newLoanAmount}
              onClick={async () => {
                try {
                  setSavingLoan(true);
                  await soulManager.createPersonalLoan({ amount: newLoanAmount, description: newLoanDesc, date: newLoanDate });
                  const loans = await soulManager.getPersonalLoans();
                  setPersonalLoans(loans ?? []);
                  setNewLoanAmount(0);
                  setNewLoanDesc("");
                } finally {
                  setSavingLoan(false);
                }
              }}
              sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: DARK, color: "white", boxShadow: "none" }}>
              {savingLoan ? "Guardando..." : "Registrar préstamo"}
            </Button>
          </Stack>

          {/* Historial */}
          {personalLoans.length > 0 && (
            <Stack spacing={1}>
              {personalLoans.map((loan) => (
                <Box key={loan.id} sx={{ bgcolor: "white", borderRadius: 2, p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#ef4444" }}>{fmtMoney(loan.amount)}</Typography>
                    <Typography sx={{ fontSize: 11, color: "#8899AA" }}>{loan.description} · {loan.date}</Typography>
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.5}>
                    <Chip label={loan.status === "PENDING" ? "Pendiente" : "Pagado"} size="small"
                      sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: loan.status === "PENDING" ? "#ef444418" : "#00C85318", color: loan.status === "PENDING" ? "#ef4444" : "#00C853" }} />
                    {loan.status === "PENDING" && (
                      <Button size="small" onClick={async () => {
                        await soulManager.updatePersonalLoan({ id: loan.id, status: "PAID" });
                        const loans = await soulManager.getPersonalLoans();
                        setPersonalLoans(loans ?? []);
                      }}
                        sx={{ fontSize: 10, textTransform: "none", color: "#00C853", p: 0, minWidth: 0 }}>
                        Marcar pagado
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Apalancamiento - sección oculta */}
      <Accordion elevation={0} sx={{ borderRadius: 3, border: "1px solid #f0f0f0", "&:before": { display: "none" }, mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Lock sx={{ fontSize: 16, color: DARK }} /><Typography sx={{ fontWeight: 700, fontSize: 15, color: DARK }}>Apalancamiento</Typography></Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, bgcolor: "#f7f9f7", borderRadius: "0 0 12px 12px" }}>
          {/* Stats */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Box sx={{ bgcolor: "white", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase" }}>Total activo</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#60a5fa" }}>{fmtMoney(totalLeverageActive)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ bgcolor: "#60a5fa11", borderRadius: 2, p: 1.5, textAlign: "center", border: "1px solid #60a5fa33" }}>
                <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase" }}>Costo mensual</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#60a5fa" }}>{fmtMoney(totalLeverageCost)}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Nuevo apalancamiento */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Nuevo apalancamiento</Typography>
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <TextField fullWidth placeholder="$ Monto" size="small"
              value={newLevAmount ? newLevAmount.toLocaleString("es-CO") : ""}
              onChange={(e) => setNewLevAmount(Number(e.target.value.replace(/\D/g, "")))}
              sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <Box sx={{ display: "flex", gap: 1, bgcolor: "white", borderRadius: 2, border: "1px solid #e0e0e0", p: 1, alignItems: "center" }}>
              <IconButton size="small" onClick={() => setNewLevRate((r) => Math.max(0, Number((r - 0.5).toFixed(1))))} sx={{ bgcolor: "#f0f0f0" }}>
                <Typography sx={{ fontWeight: 800, px: 0.5 }}>−</Typography>
              </IconButton>
              <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 800, fontSize: 18, color: "#60a5fa" }}>{newLevRate.toFixed(1)}% mensual</Typography>
              <IconButton size="small" onClick={() => setNewLevRate((r) => Number((r + 0.5).toFixed(1)))} sx={{ bgcolor: "#f0f0f0" }}>
                <Typography sx={{ fontWeight: 800, px: 0.5 }}>+</Typography>
              </IconButton>
            </Box>
            <Stack direction="row" spacing={1}>
              {["propio", "tercero"].map((t) => (
                <Box key={t} onClick={() => setNewLevType(t)}
                  sx={{ flex: 1, p: 1, borderRadius: 2, textAlign: "center", cursor: "pointer", border: `2px solid ${newLevType === t ? "#60a5fa" : "#e0e0e0"}`, bgcolor: newLevType === t ? "#60a5fa15" : "white", transition: "all 0.2s" }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: newLevType === t ? "#60a5fa" : "#667788" }}>
                    {t === "propio" ? <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><AttachMoney sx={{ fontSize: 14 }} />Dinero propio</Box> : <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><PeopleAlt sx={{ fontSize: 14 }} />De tercero</Box>}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <TextField fullWidth placeholder="Descripción" size="small"
              value={newLevDesc} onChange={(e) => setNewLevDesc(e.target.value)}
              sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <TextField fullWidth type="date" size="small"
              value={newLevDate} onChange={(e) => setNewLevDate(e.target.value)}
              sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <Button fullWidth variant="contained" disabled={savingLev || !newLevAmount}
              onClick={async () => {
                try {
                  setSavingLev(true);
                  await soulManager.createLeverage({ amount: newLevAmount, interestRate: newLevRate, description: newLevDesc, date: newLevDate, type: newLevType });
                  const levs = await soulManager.getLeverages();
                  setLeverages(levs ?? []);
                  setNewLevAmount(0);
                  setNewLevDesc("");
                } finally {
                  setSavingLev(false);
                }
              }}
              sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: "#60a5fa", color: "white", boxShadow: "none", "&:hover": { bgcolor: "#3b82f6" } }}>
              {savingLev ? "Guardando..." : "Registrar apalancamiento"}
            </Button>
          </Stack>

          {/* Historial */}
          {leverages.length > 0 && (
            <Stack spacing={1}>
              {leverages.map((lev) => (
                <Box key={lev.id} sx={{ bgcolor: "white", borderRadius: 2, p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#60a5fa" }}>{fmtMoney(lev.amount)}</Typography>
                    <Typography sx={{ fontSize: 11, color: "#8899AA" }}>{lev.interestRate}% · {lev.type === "propio" ? "Propio" : "Tercero"} · {lev.date}</Typography>
                    {lev.status === "ACTIVE" && lev.type === "tercero" && (() => {
                      const meses = Math.max(1, Math.round((new Date().getTime() - new Date(lev.date).getTime()) / (1000 * 60 * 60 * 24 * 30)));
                      const costoAcumulado = Math.round(lev.amount * (lev.interestRate / 100) * meses);
                      return <Typography sx={{ fontSize: 11, color: "#ef4444", fontWeight: 600 }}>Costo acumulado: {fmtMoney(costoAcumulado)} ({meses} mes{meses > 1 ? "es" : ""})</Typography>;
                    })()}
                    {lev.description && <Typography sx={{ fontSize: 11, color: "#8899AA" }}>{lev.description}</Typography>}
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.5}>
                    <Chip label={lev.status === "ACTIVE" ? "Activo" : "Pagado"} size="small"
                      sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: lev.status === "ACTIVE" ? "#60a5fa18" : "#00C85318", color: lev.status === "ACTIVE" ? "#60a5fa" : "#00C853" }} />
                    {lev.status === "ACTIVE" && (
                      <Button size="small" onClick={async () => {
                        await soulManager.updateLeverage({ id: lev.id, status: "PAID" });
                        const levs = await soulManager.getLeverages();
                        setLeverages(levs ?? []);
                      }}
                        sx={{ fontSize: 10, textTransform: "none", color: "#00C853", p: 0, minWidth: 0 }}>
                        Marcar pagado
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Estados de créditos */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #f0f0f0" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: DARK, mb: 2 }}>Estados de créditos</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <PieChart
                width={180}
                height={180}
                colors={[GREEN, "#8899AA", "#f59e0b", "#ef4444"]}
                series={[{
                  data: [
                    { id: 0, value: approved.length, label: "Aprobados" },
                    { id: 1, value: finished.length, label: "Finalizados" },
                    { id: 2, value: pending.length, label: "Solicitudes" },
                    { id: 3, value: defaulted.length, label: "Pendientes" },
                  ],
                  innerRadius: 40,
                  outerRadius: 75,
                  paddingAngle: 4,
                  cornerRadius: 3,
                  arcLabel: (item) => item.value > 0 ? `${item.value}` : "",
                  cx: 85,
                  cy: 85,
                }]}
                slotProps={{ legend: { hidden: true } }}
              />
            </Grid>
            <Grid item xs={6}>
              <Stack spacing={1.5}>
                {[
                  { label: "Aprobados", value: approved.length, color: GREEN },
                  { label: "Finalizados", value: finished.length, color: "#8899AA" },
                  { label: "Solicitudes", value: pending.length, color: "#f59e0b" },
                  { label: "Pendientes", value: defaulted.length, color: "#ef4444" },
                ].map((item) => (
                  <Stack key={item.label} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color }} />
                      <Typography sx={{ fontSize: 12, color: "#667788" }}>{item.label}</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: item.color }}>{item.value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AnalyticsPage;
